import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";

const extractedCatalogHeadings = [
  { label: "Row No", field: "row_number", col_width: "100px" },
  {
    label: "Filter",
    field: "filter_name",
    col_width: "220px",
    cellType: "readMore",
    maxLength: 38,
  },
  { label: "Item ID", field: "item_id", col_width: "140px" },
  {
    label: "Item Name",
    field: "item_name",
    col_width: "260px",
    cellType: "readMore",
    maxLength: 48,
  },
  { label: "Category", field: "category", col_width: "150px" },
  { label: "Subcategory", field: "subcategory", col_width: "170px" },
  { label: "Brand", field: "brand", col_width: "140px" },
  { label: "Unit Price/MRP", field: "unit_price", col_width: "150px" },
  { label: "Currency", field: "currency", col_width: "110px" },
  { label: "Stock Qty", field: "stock_qty", col_width: "120px" },
  {
    label: "Supplier",
    field: "supplier",
    col_width: "200px",
    cellType: "readMore",
    maxLength: 34,
  },
  { label: "Status", field: "status", col_width: "130px" },
];

const getValue = (item, keys, fallback = "-") => {
  const matchedKey = keys.find(
    (key) =>
      item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "",
  );
  return matchedKey ? item[matchedKey] : fallback;
};

const formatDisplayNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  const numberValue = Number(normalizedValue);
  return Number.isFinite(numberValue)
    ? numberValue.toLocaleString("en-IN")
    : String(value);
};

const buildExtractedCatalogRows = (items) =>
  (Array.isArray(items) ? items : []).map((item, index) => ({
    token: `${getValue(item, ["filter_id"], "filter")}-${getValue(
      item,
      ["row_number", "Line No"],
      index + 1,
    )}-${index}`,
    row_number: getValue(item, ["row_number", "Line No"], index + 1),
    filter_name: getValue(item, ["filter_name", "name"]),
    item_id: getValue(item, ["Item ID", "item_id", "item_code"]),
    item_name: getValue(item, ["Item Name", "item_name", "name"]),
    category: getValue(item, ["Category", "category"]),
    subcategory: getValue(item, ["Subcategory", "subcategory"]),
    brand: getValue(item, ["Brand", "brand"]),
    unit_price: formatDisplayNumber(
      getValue(
        item,
        ["Unit Price", "MRP", "unit_price", "unit_rate", "rate", "mrp"],
        "",
      ),
    ),
    currency: getValue(item, ["Currency", "currency"]),
    stock_qty: formatDisplayNumber(
      getValue(item, ["Stock Qty", "stock_qty", "quantity"], ""),
    ),
    supplier: getValue(item, [
      "Supplier",
      "supplier",
      "supplier_name",
      "vendor_name",
    ]),
    status: getValue(item, ["Status", "status"]),
  }));

const normalizeFilteredCatalogResponse = (payload, priceCatalogFile, filterDocumentFile) => {
  const matchedItems = Array.isArray(payload?.matched_items)
    ? payload.matched_items
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return {
    ...payload,
    document_number:
      payload?.filter_file ||
      payload?.catalog_file ||
      filterDocumentFile?.name ||
      priceCatalogFile?.name ||
      "Generated Quotation",
    buyer_name: payload?.buyer_name || "-",
    vendor_name: payload?.vendor_name || payload?.supplier_name || "-",
    created_date: new Date().toISOString().split("T")[0],
    matched_count: payload?.matched_count ?? matchedItems.length,
    price_catalog_file_name: priceCatalogFile?.name || payload?.catalog_file || "",
    filter_document_file_name: filterDocumentFile?.name || payload?.filter_file || "",
    line_items: matchedItems,
    raw_filter_response: payload,
  };
};

const PriceListUploadPage = ({ onBack, onSave }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const priceListInputRef = useRef(null);
  const filterInputRef = useRef(null);

  const [priceListFile, setPriceListFile] = useState(null);
  const [filterFile, setFilterFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [showPriceListPreview, setShowPriceListPreview] = useState(false);
  const [showFilterPreview, setShowFilterPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);

  const extractedCatalogRows = useMemo(
    () => buildExtractedCatalogRows(extractedData?.line_items),
    [extractedData],
  );

  const organizationToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.org_token ||
    "";
  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user?.auth_token ||
    "";

  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }
    setExtractedData(null);
    setExtractError("");
    setSaveError("");
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }
    setExtractedData(null);
    setExtractError("");
    setSaveError("");
  };

  const handleClearFile = (type) => {
    if (type === "priceList") {
      setPriceListFile(null);
      setShowPriceListPreview(false);
      if (priceListInputRef.current) priceListInputRef.current.value = "";
    } else {
      setFilterFile(null);
      setShowFilterPreview(false);
      if (filterInputRef.current) filterInputRef.current.value = "";
    }
    setExtractedData(null);
    setExtractError("");
    setSaveError("");
  };

  const handleExtract = async () => {
    if (!priceListFile || !filterFile) return;
    try {
      setLoading(true);
      setExtractError("");
      setSaveError("");

      const formData = new FormData();
      formData.append("filter_document", filterFile);
      formData.append("price_catalog", priceListFile);

      const response = await axios.post(API_ENDPOINT.filter_price_catalog, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const payload = response?.data || {};
      if (payload?.status === false || payload?.success === false) {
        setExtractError(payload?.message || "Unable to generate quotation.");
        setExtractedData(null);
        return;
      }

      setExtractedData(
        normalizeFilteredCatalogResponse(payload, priceListFile, filterFile),
      );
    } catch (error) {
      console.error("Error filtering price catalog:", error);
      setExtractError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to generate quotation. Please try again.",
      );
      setExtractedData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData) return;

    if (!organizationToken) {
      setSaveError("Organization token not found. Please login again.");
      return;
    }

    try {
      setSaveLoading(true);
      setSaveError("");

      const headers = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers.Authorization = `Token ${authToken}`;
      }

      const response = await axios.post(
        API_ENDPOINT.save_price_catalog_filter,
        {
          organization_token: organizationToken,
          filter_result: extractedData.raw_filter_response || extractedData,
        },
        { headers },
      );

      const payload = response?.data || {};
      if (payload?.status === false || payload?.success === false) {
        setSaveError(payload?.message || "Unable to save filtered catalog.");
        return;
      }

      const preservedItems =
        Array.isArray(payload?.matched_items) && payload.matched_items.length > 0
          ? payload.matched_items
          : extractedData.line_items;

      const nextSavedRecord = {
        ...extractedData,
        ...payload,
        document_number: extractedData.document_number,
        buyer_name: extractedData.buyer_name,
        vendor_name: extractedData.vendor_name,
        created_date: extractedData.created_date,
        matched_count: payload?.matched_count ?? extractedData.matched_count,
        line_items: preservedItems,
        raw_filter_response: extractedData.raw_filter_response,
        save_response: payload,
      };

      setSavedRecord(nextSavedRecord);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error saving filtered price catalog:", error);
      setSaveError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to save filtered catalog. Please try again.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onSave(savedRecord || extractedData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Create Quotation" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Upload Price List
          </Typography>

          <Box
            onDrop={(e) => handleDrop(e, "priceList")}
            onDragOver={handleDragOver}
            sx={{
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {priceListFile ? priceListFile.name : "Drag and drop file here"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => priceListInputRef.current?.click()}
              >
                Browse
              </Button>

              {priceListFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowPriceListPreview((prev) => !prev)}
                  >
                    {showPriceListPreview ? "Hide Preview" : "Preview"}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => handleClearFile("priceList")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Filter Record
          </Typography>

          <Box
            onDrop={(e) => handleDrop(e, "filter")}
            onDragOver={handleDragOver}
            sx={{
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {filterFile ? filterFile.name : "Drag and drop file here"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => filterInputRef.current?.click()}
              >
                Browse
              </Button>

              {filterFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowFilterPreview((prev) => !prev)}
                  >
                    {showFilterPreview ? "Hide Preview" : "Preview"}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => handleClearFile("filter")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <input
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        ref={priceListInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "priceList")}
      />
      <input
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        ref={filterInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "filter")}
      />

      {showPriceListPreview && priceListFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            Price List Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(priceListFile)}
            title="Price List Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !priceListFile || !filterFile || loading || saveLoading ? 0.7 : 1,
          }}
          onClick={handleExtract}
          disabled={!priceListFile || !filterFile || loading || saveLoading}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Generate Quotation"}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !extractedData || loading || saveLoading ? 0.7 : 1,
          }}
          onClick={handleSave}
          disabled={!extractedData || loading || saveLoading}
        >
          {saveLoading ? <CircularProgress size={16} color="inherit" /> : "Save"}
        </Button>
      </Box>

      {extractError ? (
        <Typography sx={{ color: "#b91c1c", fontSize: 14, mt: 1 }}>
          {extractError}
        </Typography>
      ) : null}
      {saveError ? (
        <Typography sx={{ color: "#b91c1c", fontSize: 14, mt: 1 }}>
          {saveError}
        </Typography>
      ) : null}

      {extractedData ? (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 1.5,
              mb: 2,
              p: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              backgroundColor: "#f8fafc",
            }}
          >
            {[
              [
                "Catalog File",
                extractedData.catalog_file ||
                  extractedData.price_catalog_file_name ||
                  "-",
              ],
              [
                "Filter File",
                extractedData.filter_file ||
                  extractedData.filter_document_file_name ||
                  "-",
              ],
              ["Filter Sets", extractedData.filter_set_count ?? "-"],
              [
                "Matched Items",
                extractedData.matched_count ?? extractedCatalogRows.length,
              ],
              ["Unique Items", extractedData.unique_matched_count ?? "-"],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: 14,
                    fontWeight: 600,
                    wordBreak: "break-word",
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <DataTable
            headings={extractedCatalogHeadings}
            tableData={extractedCatalogRows}
            tableSize="large"
            fixedLayout
            stickyHead
            hideDefaultEmptyMessage
          />
        </Box>
      ) : null}

      <Modal open={showSuccessModal}>
        <Fade in={showSuccessModal}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Saved Successfully!"
              popupDescriptionLG="Filtered catalog data has been saved."
              popBtn
              buyerBtn
              buyerBtnOutline
              btnText="Okay"
              btnClick={handleSuccessClose}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default PriceListUploadPage;
